/** The assertion table: every claim, with all four confidence axes and its evidence. */

import { useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type { AssertionResponse } from '@/lib/schemas';
import { Badge, ConfidenceBadge, ErrorState, Loading, ProvenanceLink, Table } from '@/components/ui';

/** The results tab for one case. */
export function ResultsTab({ caseId }: { caseId: string }): ReactElement {
  // **Filters, applied in the database.** These were client-side, which meant fetching
  // every claim to throw most away — and, worse, the list was capped at `limit`, so a
  // filter over a large case narrowed a truncated set rather than the case.
  const [predicate, setPredicate] = useState('');
  const [minReliability, setMinReliability] = useState('');
  const [special, setSpecial] = useState('');

  const assertions = useQuery({
    queryKey: ['assertions', caseId, predicate, minReliability, special],
    queryFn: () =>
      request<AssertionResponse[]>(`/cases/${caseId}/assertions`, {
        query: {
          limit: 200,
          predicate: predicate || undefined,
          min_reliability: minReliability || undefined,
          special_category: special === '' ? undefined : special === 'yes',
        },
      }),
  });

  // The dropdown's options come from an unfiltered read. Deriving them from the filtered
  // response would collapse the list to whatever is currently selected, leaving no way
  // back to the others.
  const vocabulary = useQuery({
    queryKey: ['assertion-predicates', caseId],
    queryFn: () =>
      request<AssertionResponse[]>(`/cases/${caseId}/assertions`, { query: { limit: 200 } }),
  });

  if (assertions.isLoading) return <Loading />;
  if (assertions.error) return <ErrorState error={assertions.error} />;

  const items = assertions.data ?? [];
  const predicates = [...new Set((vocabulary.data ?? []).map((item) => item.predicate))].sort();

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <select
          data-testid="filter-predicate"
          value={predicate}
          onChange={(event) => setPredicate(event.target.value)}
          className="rounded border border-line bg-surface px-2 py-1"
        >
          <option value="">All predicates</option>
          {predicates.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>

        {/* The scale runs A best through F worst, so "B or better" keeps A and B. */}
        <select
          data-testid="filter-reliability"
          value={minReliability}
          onChange={(event) => setMinReliability(event.target.value)}
          className="rounded border border-line bg-surface px-2 py-1"
        >
          <option value="">Any source reliability</option>
          {['A', 'B', 'C', 'D'].map((grade) => (
            <option key={grade} value={grade}>
              {grade} or better
            </option>
          ))}
        </select>

        <select
          data-testid="filter-special"
          value={special}
          onChange={(event) => setSpecial(event.target.value)}
          className="rounded border border-line bg-surface px-2 py-1"
        >
          <option value="">Any category</option>
          <option value="yes">Article 9 only</option>
          <option value="no">Exclude Article 9</option>
        </select>

        <span className="text-xs text-muted">{items.length} claims</span>
      </div>

      <Table
        headers={['Claim', 'Value', 'Confidence', 'Source', 'Evidence']}
        empty="No claims match this filter."
        rows={items.map((item) => [
          <span key="p" data-testid="claim-predicate" className="font-mono text-xs">
            {item.predicate}
          </span>,
          <span key="v" className="flex items-center gap-2">
            {item.object_value}
            {item.is_special_category && <Badge tone="warn">Art. 9</Badge>}
          </span>,
          <ConfidenceBadge
            key="c"
            sourceReliability={item.confidence.source_reliability}
            informationCredibility={item.confidence.information_credibility}
            extractionConfidence={item.confidence.extraction_confidence}
          />,
          <span key="s" className="text-xs text-muted">
            {item.provenance.plugin_name}
          </span>,
          <ProvenanceLink key="e" caseId={caseId} assertionId={item.id} />,
        ])}
      />
    </div>
  );
}
